import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Paper, Stack, Chip, CircularProgress, Alert,
    Accordion, AccordionSummary, AccordionDetails, Divider
} from '@mui/material';
import { Car, Plane, Train, Bus, HelpCircle, ChevronDown, MapPin } from 'lucide-react';
import { getRetreatTravelHelp } from '../../../Services/Retreats/RetreatApis';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const getGoodToKnowTitle = (type) => {
    switch (type) {
        case 'visa': return 'Visa Requirements';
        case 'what_to_bring': return 'What to Bring';
        case 'vaccinations': return 'Vaccination Requirements';
        case 'insurance': return 'Travel Insurance';
        default: return type
            ? type.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
            : 'Travel Info';
    }
};

const TransportIcon = ({ mode, size = 16 }) => {
    const color = '#FFBD65';
    if (mode === 'flight') return <Plane size={size} color={color} />;
    if (mode === 'train') return <Train size={size} color={color} />;
    if (mode === 'bus') return <Bus size={size} color={color} />;
    return <Car size={size} color={color} />;
};

// ─── Shared primitives (same design language as all other tabs) ───────────────

const SectionTitle = ({ icon: Icon, children }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <Icon size={15} color="#FFBD65" strokeWidth={2.2} />
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1E293B', letterSpacing: '0.02em' }}>
            {children}
        </Typography>
    </Box>
);

const InfoCard = ({ children, sx }) => (
    <Paper
        elevation={0}
        sx={{ border: '1px solid #E8EEF4', borderRadius: '10px', bgcolor: '#FFFFFF', overflow: 'hidden', ...sx }}
    >
        {children}
    </Paper>
);

const StatusBadge = ({ active, trueLabel = 'Available', falseLabel = 'Unavailable' }) => (
    <Chip
        label={active ? trueLabel : falseLabel}
        size="small"
        sx={{
            height: 20, fontSize: '0.7rem', fontWeight: 600,
            bgcolor: active ? '#F0FDF4' : '#FFF1F2',
            color: active ? '#16A34A' : '#E11D48',
            border: `1px solid ${active ? '#BBF7D0' : '#FECDD3'}`,
        }}
    />
);

const cardPx = { xs: 2, sm: 2.5 };

// ─── Main component ───────────────────────────────────────────────────────────

const TravelHelpTab = ({ retreat }) => {
    const [retreatTravelHelp, setRetreatTravelHelp] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [expandedFAQ, setExpandedFAQ] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            if (!retreat?.id) return;
            try {
                setLoading(true);
                const response = await getRetreatTravelHelp(retreat.id);
                if (response?.success) {
                    setRetreatTravelHelp(response.data);
                } else {
                    setError('Failed to fetch travel information.');
                }
            } catch (err) {
                console.error('Error fetching retreat travel help:', err);
                setError('An error occurred while fetching travel information.');
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [retreat?.id]);

    const handleFAQToggle = (id) => setExpandedFAQ(expandedFAQ === id ? null : id);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress size={28} sx={{ color: '#FFBD65' }} />
            </Box>
        );
    }

    if (error) {
        return <Alert severity="error" sx={{ borderRadius: '8px' }}>{error}</Alert>;
    }

    if (!retreatTravelHelp) {
        return (
            <InfoCard sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">No travel information available.</Typography>
            </InfoCard>
        );
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>

            {/* ── 1. Transportation Options ─────────────────────────────────── */}
            {retreatTravelHelp.transportInfo?.length > 0 && (
                <Box>
                    <SectionTitle icon={Car}>Transportation Options</SectionTitle>
                    <InfoCard>
                        {/* Table header */}
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: { xs: '32px 1fr auto', sm: '32px 1fr 1fr auto' },
                                px: cardPx, py: 1,
                                bgcolor: '#F8FAFC',
                                borderBottom: '1px solid #F1F5F9',
                                gap: { xs: 1.5, sm: 2 },
                                alignItems: 'center',
                            }}
                        >
                            <Box /> {/* icon col */}
                            <Typography variant="caption" sx={{ color: '#94A3B8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Mode</Typography>
                            <Typography variant="caption" sx={{ color: '#94A3B8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', display: { xs: 'none', sm: 'block' } }}>Description</Typography>
                            <Typography variant="caption" sx={{ color: '#94A3B8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'right' }}>Status</Typography>
                        </Box>

                        {retreatTravelHelp.transportInfo.map((transport, idx) => (
                            <Box key={transport.id}>
                                <Box
                                    sx={{
                                        px: cardPx, py: 1.5,
                                        display: 'grid',
                                        gridTemplateColumns: { xs: '32px 1fr auto', sm: '32px 1fr 1fr auto' },
                                        gap: { xs: 1.5, sm: 2 },
                                        alignItems: 'flex-start',
                                    }}
                                >
                                    {/* Mode icon */}
                                    <Box
                                        sx={{
                                            width: 32, height: 32, borderRadius: '8px',
                                            bgcolor: '#FFF7ED', border: '1px solid #FED7AA',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                                        }}
                                    >
                                        <TransportIcon mode={transport.mode} size={15} />
                                    </Box>

                                    {/* Mode label + mobile description */}
                                    <Box>
                                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#1E293B', textTransform: 'capitalize' }}>
                                            {transport.mode || '—'}
                                        </Typography>
                                        {transport.description && (
                                            <Box
                                                sx={{
                                                    display: { xs: 'block', sm: 'none' },
                                                    color: '#64748B', fontSize: '0.78rem', lineHeight: 1.5, mt: 0.3,
                                                    '& *': { fontFamily: 'inherit !important', fontSize: 'inherit !important', color: 'inherit !important' },
                                                }}
                                                dangerouslySetInnerHTML={{ __html: transport.description }}
                                            />
                                        )}
                                    </Box>

                                    {/* Description — desktop only */}
                                    {transport.description ? (
                                        <Box
                                            sx={{
                                                display: { xs: 'none', sm: 'block' },
                                                color: '#64748B', fontSize: '0.82rem', lineHeight: 1.55,
                                                '& *': { fontFamily: 'inherit !important', fontSize: 'inherit !important', color: 'inherit !important' },
                                            }}
                                            dangerouslySetInnerHTML={{ __html: transport.description }}
                                        />
                                    ) : (
                                        <Typography variant="body2" sx={{ color: '#CBD5E1', display: { xs: 'none', sm: 'block' } }}>—</Typography>
                                    )}

                                    {/* Status badge */}
                                    <Box sx={{ textAlign: 'right' }}>
                                        <StatusBadge active={transport.isEnabled} />
                                    </Box>
                                </Box>
                                {idx < retreatTravelHelp.transportInfo.length - 1 && <Divider />}
                            </Box>
                        ))}
                    </InfoCard>
                </Box>
            )}

            {/* ── 2. Airport & Station Transfers ────────────────────────────── */}
            {retreatTravelHelp.transfers?.length > 0 && (
                <Box>
                    <SectionTitle icon={Plane}>Airport & Station Transfers</SectionTitle>
                    <InfoCard>
                        {/* Table header */}
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: { xs: '1fr', sm: '1fr auto' },
                                px: cardPx, py: 1,
                                bgcolor: '#F8FAFC',
                                borderBottom: '1px solid #F1F5F9',
                                gap: 2,
                            }}
                        >
                            <Typography variant="caption" sx={{ color: '#94A3B8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Location / Airport</Typography>
                            <Typography variant="caption" sx={{ color: '#94A3B8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', display: { xs: 'none', sm: 'block' }, textAlign: 'right' }}>Details</Typography>
                        </Box>

                        {retreatTravelHelp.transfers.map((transfer, idx) => (
                            <Box key={transfer.id}>
                                <Box
                                    sx={{
                                        px: cardPx, py: 1.5,
                                        display: 'grid',
                                        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                                        gap: { xs: 1, sm: 2 },
                                        alignItems: 'center',
                                    }}
                                >
                                    {/* Airport info */}
                                    <Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.25 }}>
                                            <MapPin size={13} color="#94A3B8" />
                                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#1E293B' }}>
                                                {transfer.airport?.airportName || 'Unnamed Location'}
                                                {transfer.airport?.iataCode && (
                                                    <Typography component="span" variant="caption" sx={{ ml: 0.75, color: '#94A3B8', fontWeight: 500 }}>
                                                        ({transfer.airport.iataCode})
                                                    </Typography>
                                                )}
                                            </Typography>
                                        </Box>
                                        {transfer.airport?.location && (
                                            <Typography variant="caption" sx={{ color: '#94A3B8', pl: '21px' }}>
                                                {transfer.airport.location}
                                            </Typography>
                                        )}
                                    </Box>

                                    {/* Chips — stacked on mobile, right-aligned on desktop */}
                                    <Stack
                                        direction="row"
                                        flexWrap="wrap"
                                        gap={0.75}
                                        sx={{ justifyContent: { xs: 'flex-start', sm: 'flex-end' } }}
                                    >
                                        {transfer.transferType && (
                                            <Chip
                                                label={transfer.transferType.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                                                size="small"
                                                sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600, bgcolor: '#EFF6FF', color: '#1D4ED8', border: '1px solid #BFDBFE' }}
                                            />
                                        )}
                                        <Chip
                                            label={transfer.isFree ? 'Free Transfer' : `${transfer.cost} ${transfer.currency}`}
                                            size="small"
                                            sx={{
                                                height: 20, fontSize: '0.7rem', fontWeight: 600,
                                                bgcolor: transfer.isFree ? '#F0FDF4' : '#FFFBEB',
                                                color: transfer.isFree ? '#16A34A' : '#92400E',
                                                border: `1px solid ${transfer.isFree ? '#BBF7D0' : '#FDE68A'}`,
                                            }}
                                        />

                                        <Chip
                                            label={transfer.isAirportTransferProvided === true ? "Transfer Provided" : "Transfer Not Provided"}
                                            size="small"
                                            sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600, bgcolor: '#EFF6FF', color: '#1D4ED8', border: '1px solid #BFDBFE' }}
                                        />

                                        {transfer.bookAtCheckout && (
                                            <Chip
                                                label="Book at Checkout"
                                                size="small"
                                                sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600, bgcolor: '#F5F3FF', color: '#6D28D9', border: '1px solid #DDD6FE' }}
                                            />
                                        )}
                                    </Stack>
                                </Box>
                                {idx < retreatTravelHelp.transfers.length - 1 && <Divider />}
                            </Box>
                        ))}
                    </InfoCard>
                </Box>
            )}

            {/* ── 3. Good to Know ───────────────────────────────────────────── */}
            {retreatTravelHelp.goodToKnow?.length > 0 && (
                <Box>
                    <SectionTitle icon={HelpCircle}>Travel — Good to Know</SectionTitle>
                    <Stack spacing={1}>
                        {retreatTravelHelp.goodToKnow.map((info) => (
                            <Accordion
                                key={info.id}
                                expanded={expandedFAQ === `goodToKnow-${info.id}`}
                                onChange={() => handleFAQToggle(`goodToKnow-${info.id}`)}
                                elevation={0}
                                sx={{
                                    border: '1px solid #E8EEF4',
                                    borderRadius: '10px !important',
                                    bgcolor: '#FFFFFF',
                                    '&:before': { display: 'none' },
                                    '&.Mui-expanded': { margin: 0 },
                                }}
                            >
                                <AccordionSummary
                                    expandIcon={<ChevronDown size={16} color="#94A3B8" />}
                                    sx={{ px: cardPx, py: 0.5, minHeight: 48, '& .MuiAccordionSummary-content': { my: 1 } }}
                                >
                                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#1E293B' }}>
                                        {getGoodToKnowTitle(info.type)}
                                    </Typography>
                                </AccordionSummary>
                                <AccordionDetails
                                    sx={{
                                        px: cardPx, py: 1.5,
                                        borderTop: '1px solid #F1F5F9',
                                        bgcolor: '#FAFBFC',
                                    }}
                                >
                                    <Box
                                        sx={{
                                            color: '#475569', fontSize: '0.85rem', lineHeight: 1.65,
                                            '& p': { mb: 1 },
                                            '& ul, & ol': { pl: '1.2rem', mb: 1, '& li': { mb: 0.5 } },
                                            '& *': { fontFamily: 'inherit !important', fontSize: 'inherit !important', color: 'inherit !important' },
                                        }}
                                        dangerouslySetInnerHTML={{ __html: info.content || '' }}
                                    />
                                </AccordionDetails>
                            </Accordion>
                        ))}
                    </Stack>
                </Box>
            )}

            {/* ── 4. FAQs ───────────────────────────────────────────────────── */}
            {retreatTravelHelp.faqs?.length > 0 && (
                <Box>
                    <SectionTitle icon={HelpCircle}>Frequently Asked Questions</SectionTitle>
                    <Stack spacing={1}>
                        {retreatTravelHelp.faqs.map((faq) => (
                            <Accordion
                                key={faq.id}
                                expanded={expandedFAQ === `faq-${faq.id}`}
                                onChange={() => handleFAQToggle(`faq-${faq.id}`)}
                                elevation={0}
                                sx={{
                                    border: '1px solid #E8EEF4',
                                    borderRadius: '10px !important',
                                    bgcolor: '#FFFFFF',
                                    '&:before': { display: 'none' },
                                    '&.Mui-expanded': { margin: 0 },
                                }}
                            >
                                <AccordionSummary
                                    expandIcon={<ChevronDown size={16} color="#94A3B8" />}
                                    sx={{ px: cardPx, py: 0.5, minHeight: 48, '& .MuiAccordionSummary-content': { my: 1 } }}
                                >
                                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#1E293B' }}>
                                        {faq.question}
                                    </Typography>
                                </AccordionSummary>
                                <AccordionDetails
                                    sx={{
                                        px: cardPx, py: 1.5,
                                        borderTop: '1px solid #F1F5F9',
                                        bgcolor: '#FAFBFC',
                                    }}
                                >
                                    <Box
                                        sx={{
                                            color: '#475569', fontSize: '0.85rem', lineHeight: 1.65,
                                            '& p': { mb: 1 },
                                            '& ul, & ol': { pl: '1.2rem', mb: 1, '& li': { mb: 0.5 } },
                                            '& *': { fontFamily: 'inherit !important', fontSize: 'inherit !important', color: 'inherit !important' },
                                        }}
                                        dangerouslySetInnerHTML={{ __html: faq.answer || '' }}
                                    />
                                </AccordionDetails>
                            </Accordion>
                        ))}
                    </Stack>
                </Box>
            )}

            {/* 5. furtherInformation details */}
            {retreatTravelHelp.furtherInformation?.length > 0 && (
                <Box>
                    <SectionTitle icon={HelpCircle}>Further Information</SectionTitle>
                    <Box
                        sx={{
                            color: '#475569', fontSize: '0.85rem', lineHeight: 1.65,
                            '& p': { mb: 1 },
                            '& ul, & ol': { pl: '1.2rem', mb: 1, '& li': { mb: 0.5 } },
                            '& *': { fontFamily: 'inherit !important', fontSize: 'inherit !important', color: 'inherit !important' },
                        }}
                        dangerouslySetInnerHTML={{ __html: retreatTravelHelp.furtherInformation || '' }}
                    />
                </Box>
            )}

        </Box>
    );
};

export default TravelHelpTab;
